import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getApproverMaker } from 'src/clients';
import { getRules } from 'src/clients/BusinessPayment/ProductWorkflow/rules';
import { useAppSelector } from 'src/hooks';
import useSWR from 'swr';
import { ProductWorkflowRule } from '../constants';
import {
  fetchActiveMultiApproval,
  fetchChildMultiApproval,
  fetchIsParentApprovalActiveRules,
  fetchMultiApproval,
  submitChildMultiApproval,
  submitMultiApproval,
} from '../redux/actions';
import {
  getChildSubmitMultiApprovalSelector,
  getFetchActiveMultiApprovalSelector,
  getFetchChildMultiApprovalSelector,
  getFetchIsParentActiveMultiApprovalSelector,
  getFetchMultiApprovalSelector,
  getSubmitMultiApprovalSelector,
} from '../redux/selectors';
import {
  SubmitChiltMultiApprovalData,
  SubmitMultiApprovalData,
} from '../types';

export const useYourMultiApprovalData = () => {
  const dispatch = useDispatch();
  const [shouldFetch, setShouldFetch] = useState(true);
  const { data, loading, isSuccess } = useSelector(
    getFetchMultiApprovalSelector,
  );

  useEffect(() => {
    if (shouldFetch) {
      setShouldFetch(false);
      // TODO Dashboard - Use swr for data fetching
      dispatch(fetchMultiApproval());
    }
  }, [dispatch, shouldFetch]);

  const triggerFetchMultiApproval = () => {
    setShouldFetch(true);
  };

  return { data, loading, isSuccess, triggerFetchMultiApproval };
};

export const useChildMultiApprovalData = () => {
  const dispatch = useDispatch();
  const [shouldFetch, setShouldFetch] = useState(true);
  const { data, loading, isSuccess } = useSelector(
    getFetchChildMultiApprovalSelector,
  );

  useEffect(() => {
    if (shouldFetch) {
      setShouldFetch(false);
      // TODO Dashboard - Use swr for data fetching
      dispatch(fetchChildMultiApproval());
    }
  }, [dispatch, shouldFetch]);

  const triggerFetchChildMultiApproval = () => {
    setShouldFetch(true);
  };

  return { data, loading, isSuccess, triggerFetchChildMultiApproval };
};

export const useSubmitMultiApproval = () => {
  const dispatch = useDispatch();
  const { loading, isSuccess } = useSelector(getSubmitMultiApprovalSelector);

  const triggerSubmitMultiApproval = (payload: SubmitMultiApprovalData[]) => {
    dispatch(submitMultiApproval(payload));
  };

  return { loading, isSuccess, triggerSubmitMultiApproval };
};

export const useChildSubmitMultiApproval = () => {
  const dispatch = useDispatch();
  const { loading, isSuccess } = useSelector(
    getChildSubmitMultiApprovalSelector,
  );

  const triggerChildSubmitMultiApproval = (
    payload: SubmitChiltMultiApprovalData,
  ) => {
    dispatch(submitChildMultiApproval(payload));
  };

  return { loading, isSuccess, triggerChildSubmitMultiApproval };
};

export const useFetchActiveMultiApproval = () => {
  const dispatch = useDispatch();
  const [shouldFetch, setShouldFetch] = useState(true);
  const { data, loading, isSuccess } = useSelector(
    getFetchActiveMultiApprovalSelector,
  );

  useEffect(() => {
    if (shouldFetch) {
      setShouldFetch(false);
      // TODO Dashboard - Use swr for data fetching
      dispatch(fetchActiveMultiApproval());
    }
  }, [dispatch, shouldFetch]);

  const triggerFetchActiveMultiApproval = () => {
    setShouldFetch(true);
  };

  return { data, loading, isSuccess, triggerFetchActiveMultiApproval };
};

export const useFetchIsParentActiveMultiApproval = () => {
  const dispatch = useDispatch();
  const [shouldFetch, setShouldFetch] = useState(true);
  const { data, loading, isSuccess } = useSelector(
    getFetchIsParentActiveMultiApprovalSelector,
  );
  const isSessionExpired = useAppSelector(state => state.auth.isSessionExpired);

  useEffect(() => {
    if (shouldFetch) {
      setShouldFetch(false);
      // TODO Dashboard - Use swr for data fetching
      if (!isSessionExpired && !loading) {
        dispatch(fetchIsParentApprovalActiveRules());
      }
    }
  }, [dispatch, loading, shouldFetch, isSuccess, data, isSessionExpired]);

  const triggerFetchActiveMultiApproval = () => {
    setShouldFetch(true);
  };

  return { data, loading, isSuccess, triggerFetchActiveMultiApproval };
};

export const useFetchApprovalMultiTransferRule = () => {
  const { data, ...swr } = useSWR(
    ['pw/rule?product_name', 'BULK_DISBURSEMENT'],
    () => getRules(ProductWorkflowRule.BULK_DISBURSEMENT),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  return { data: data?.data?.data, ...swr };
};

export const useFetchApproverMaker = (isApprover: boolean) => {
  const { data, ...swr } = useSWR(
    ['approval/v2/maker-approvers?isApprover', isApprover],
    () => getApproverMaker(isApprover),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
  return { data: data?.data?.data, ...swr };
};
